/**
 * SMSWiser Country Pins hooks
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

export interface PinnedCountry {
  countryKey: string;
  countryName: string;
}

function unwrapApiData<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

export const smswiserCountryPinsKeys = {
  all: ['smswiser-country-pins'] as const,
  list: () => [...smswiserCountryPinsKeys.all, 'list'] as const,
};

export function usePinnedCountries(enabled = true) {
  return useQuery({
    queryKey: smswiserCountryPinsKeys.list(),
    queryFn: async () => {
      const res = await api.get('/smswiser/country-pins');
      const data = unwrapApiData<PinnedCountry[]>(res.data);
      return Array.isArray(data) ? data : [];
    },
    enabled,
    staleTime: 30_000,
  });
}

export function usePinCountry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (countryKey: string) => {
      const res = await api.post<{ ok: boolean }>('/smswiser/country-pins', {
        countryKey,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: smswiserCountryPinsKeys.all });
    },
  });
}

export function useUnpinCountry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (countryKey: string) => {
      const res = await api.delete<{ ok: boolean }>('/smswiser/country-pins', {
        data: { countryKey },
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: smswiserCountryPinsKeys.all });
    },
  });
}
